// Research papers page (/research): a browsable grid of free public research
// (arXiv, Fed, ECB, BIS, NBER), filterable by topic, source and country.
// Data: /api/research (backend/src/api/research.js). Papers are never hosted
// here: "Download" opens the PDF on the publisher's own website.
//
// Filters live in the URL (?q=&topic=&source=&country=&paper=), so every view
// and every paper can be shared as a link.

import '../styles/main.css';
import '../styles/features.css';
import '../styles/research.css';
import { t, lang, applyStatic, initLanguagePicker, countryName } from '../i18n/index.js';
import { initNavTools } from '../ui/navTools.js';
import { initAuth } from '../features/auth.js';
import { annotate, explain } from '../features/glossary.js';

const PAGE = 24;
const $ = (id) => document.getElementById(id);
const el = {
    q: $('rs-q'),
    source: $('rs-source'),
    country: $('rs-country'),
    topics: $('rs-topics'),
    count: $('rs-count'),
    clear: $('rs-clear'),
    grid: $('rs-grid'),
    more: $('rs-more'),
    dialog: $('rs-dialog'),
};

const filters = { q: '', topic: '', source: '', country: '' };
let offset = 0;
let total = 0;
let requestId = 0;
const papers = new Map(); // id -> paper, for the details dialog
let lastFocus = null;

const esc = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

/** Only http(s) links from the API ever become hrefs. */
function safeHref(u) {
    try {
        const url = new URL(u);
        return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
    } catch {
        return null;
    }
}

const flag = (iso2) =>
    /^[A-Z]{2}$/.test(iso2 || '') ? String.fromCodePoint(...[...iso2].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)) : '';

let enNames = null;
try {
    enNames = new Intl.DisplayNames(['en'], { type: 'region' });
} catch {
    enNames = null;
}
const nameOf = (iso2) => countryName(iso2, enNames?.of(iso2) || iso2);

const dateFmt = new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'short', year: 'numeric' });
const fmtDate = (iso) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : dateFmt.format(d);
};

async function getJSON(path) {
    const res = await fetch(`/api${path}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
}

// ---------- URL state ----------
function readURL() {
    const p = new URLSearchParams(location.search);
    for (const k of Object.keys(filters)) filters[k] = (p.get(k) || '').slice(0, 100);
    if (filters.country) filters.country = filters.country.toUpperCase();
    return p.get('paper');
}

function writeURL(paperId = null) {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) if (v) p.set(k, v);
    if (paperId) p.set('paper', paperId);
    const qs = p.toString();
    history.replaceState(null, '', `${location.pathname}${qs ? `?${qs}` : ''}`);
}

// ---------- Filters ----------
function renderTopics(facets) {
    const chip = (id, label, n) =>
        `<button type="button" class="rs-chip${filters.topic === id ? ' active' : ''}" data-topic="${esc(id)}" aria-pressed="${filters.topic === id}">${
            id ? `<span class="rs-dot rs-t-${esc(id)}"></span>` : ''
        }${esc(label)}${n != null ? ` <span class="rs-chip-n">${n}</span>` : ''}</button>`;
    el.topics.innerHTML =
        chip('', t('rs.allTopics'), facets.total) + facets.topics.map((tp) => chip(tp.id, t(`rt.${tp.id}`), tp.n)).join('');
}

function renderSelects(facets) {
    el.source.innerHTML =
        `<option value="">${esc(t('rs.allSources'))}</option>` +
        facets.sources.map((s) => `<option value="${esc(s.id)}">${esc(s.name)} (${s.n})</option>`).join('');
    const countries = facets.countries
        .map((c) => ({ ...c, name: nameOf(c.id) }))
        .sort((a, b) => a.name.localeCompare(b.name, lang));
    el.country.innerHTML =
        `<option value="">${esc(t('rs.allCountries'))}</option>` +
        countries.map((c) => `<option value="${esc(c.id)}">${flag(c.id)} ${esc(c.name)} (${c.n})</option>`).join('');
    el.source.value = filters.source;
    el.country.value = filters.country;
    // A shared link can name a country we have no papers for (yet).
    if (filters.country && el.country.value !== filters.country) {
        el.country.insertAdjacentHTML('beforeend', `<option value="${esc(filters.country)}">${flag(filters.country)} ${esc(nameOf(filters.country))} (0)</option>`);
        el.country.value = filters.country;
    }
}

function syncChips() {
    el.topics.querySelectorAll('.rs-chip').forEach((b) => {
        const on = b.dataset.topic === filters.topic;
        b.classList.toggle('active', on);
        b.setAttribute('aria-pressed', String(on));
    });
    el.clear.hidden = !Object.values(filters).some(Boolean);
}

// ---------- Cards ----------
function card(p) {
    const download = safeHref(p.pdf_url);
    const read = safeHref(p.url);
    const place = p.country_iso2 ? `${flag(p.country_iso2)} ${esc(nameOf(p.country_iso2))}` : `🌐 ${esc(t('rs.global'))}`;
    return `<article class="rs-card" data-id="${p.id}">
  <div class="rs-card-top"><span class="rs-topic"><span class="rs-dot rs-t-${esc(p.topic)}"></span>${esc(t(`rt.${p.topic}`))}</span><span class="rs-date">${esc(fmtDate(p.published_at))}</span></div>
  <h2 class="rs-card-title"><button type="button" class="rs-open" data-id="${p.id}">${esc(p.title)}</button></h2>
  ${p.authors ? `<p class="rs-authors">${esc(p.authors)}</p>` : ''}
  ${p.summary ? `<p class="rs-summary">${esc(p.summary)}</p>` : ''}
  <div class="rs-card-foot">
    <span class="rs-meta">${esc(p.source_name)} · ${place}</span>
    <span class="rs-actions">
      <button type="button" class="rs-btn rs-btn-ghost rs-open" data-id="${p.id}">${esc(t('rs.details'))}</button>
      ${
          download
              ? `<a class="rs-btn" href="${esc(download)}" target="_blank" rel="noopener noreferrer">PDF ↗</a>`
              : read
                ? `<a class="rs-btn" href="${esc(read)}" target="_blank" rel="noopener noreferrer">${esc(p.source_name)} ↗</a>`
                : ''
      }
    </span>
  </div>
</article>`;
}

async function load({ append = false } = {}) {
    const id = ++requestId;
    if (!append) offset = 0;
    const p = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
    for (const [k, v] of Object.entries(filters)) if (v) p.set(k, v);
    el.grid.setAttribute('aria-busy', 'true');
    el.more.disabled = true;
    try {
        const res = await getJSON(`/research?${p}`);
        if (id !== requestId) return; // a newer search replaced this one
        total = res.total;
        for (const paper of res.data) papers.set(String(paper.id), paper);
        const html = res.data.map(card).join('');
        if (append) el.grid.insertAdjacentHTML('beforeend', html);
        else el.grid.innerHTML = html || `<p class="rs-empty">${esc(t('rs.none'))}</p>`;
        offset += res.data.length;
        el.count.textContent = t('rs.count', { n: total.toLocaleString(lang) });
        el.more.hidden = offset >= total;
    } catch (err) {
        if (id !== requestId) return;
        console.error('[research] load failed', err);
        if (!append) el.grid.innerHTML = `<p class="rs-empty">${esc(t('err.msg'))}</p>`;
    } finally {
        if (id === requestId) {
            el.grid.removeAttribute('aria-busy');
            el.more.disabled = false;
        }
    }
}

function applyFilters() {
    syncChips();
    writeURL();
    load();
}

// ---------- Details dialog ----------
function explainer(p) {
    const ex = explain({ title: p.title, summary: p.summary, category_slug: `r-${p.topic}` });
    const terms = ex.terms
        .map((x) => `<p><b>${esc(x.label)}:</b> ${esc(x.what)}<br><span class="ex-why">${esc(t('whyMatter'))}: ${esc(x.why)}</span></p>`)
        .join('');
    return `${terms}${ex.topic ? `<p class="ex-topic">${esc(ex.topic)}</p>` : ''}<p class="ex-note">${esc(ex.disclaimer)}</p>`;
}

function openPaper(p) {
    lastFocus = document.activeElement;
    const download = safeHref(p.pdf_url);
    const read = safeHref(p.url);
    const source = esc(p.source_name);
    el.dialog.innerHTML = `<div class="rs-d-box">
  <button type="button" class="rs-d-close" aria-label="${esc(t('close'))}">✕</button>
  <p class="rs-topic"><span class="rs-dot rs-t-${esc(p.topic)}"></span>${esc(t(`rt.${p.topic}`))}</p>
  <h2 id="rs-d-title">${esc(p.title)}</h2>
  ${p.authors ? `<p class="rs-authors">${esc(p.authors)}</p>` : ''}
  <p class="rs-meta">${source} · ${esc(t('rs.published'))} ${esc(fmtDate(p.published_at))} · ${
      p.country_iso2 ? `${flag(p.country_iso2)} ${esc(nameOf(p.country_iso2))}` : `🌐 ${esc(t('rs.global'))}`
  }</p>
  ${p.summary ? `<p class="rs-d-summary">${annotate(esc(p.summary))}</p>` : ''}
  <div class="rs-d-actions">
    ${download ? `<a class="rs-btn" href="${esc(download)}" target="_blank" rel="noopener noreferrer">${esc(t('rs.download', { source: p.source_name }))} ↗</a>` : ''}
    ${read ? `<a class="rs-btn ${download ? 'rs-btn-ghost' : ''}" href="${esc(read)}" target="_blank" rel="noopener noreferrer">${esc(t('rs.read', { source: p.source_name }))} ↗</a>` : ''}
    ${p.country_iso2 ? `<a class="rs-btn rs-btn-ghost" href="/app?country=${esc(p.country_iso2)}">${esc(t('rs.onGlobe', { country: nameOf(p.country_iso2) }))}</a>` : ''}
    <button type="button" class="rs-btn rs-btn-ghost rs-copy">${esc(t('rs.copy'))}</button>
  </div>
  <button type="button" class="news-card-explain rs-explain" aria-expanded="false">${esc(t('explain'))}</button>
  <div class="news-card-explainer" hidden></div>
</div>`;
    el.dialog.hidden = false;
    document.body.classList.add('rs-locked');
    writeURL(p.id);

    const box = el.dialog.querySelector('.news-card-explainer');
    const btn = el.dialog.querySelector('.rs-explain');
    btn.addEventListener('click', () => {
        const open = box.hidden;
        if (open && !box.innerHTML) box.innerHTML = explainer(p);
        box.hidden = !open;
        btn.setAttribute('aria-expanded', String(open));
        btn.textContent = open ? t('hideExplain') : t('explain');
    });
    el.dialog.querySelector('.rs-copy').addEventListener('click', async (e) => {
        try {
            await navigator.clipboard.writeText(location.href);
            e.target.textContent = t('rs.copied');
        } catch {
            /* clipboard blocked: the URL bar already has the link */
        }
    });
    el.dialog.querySelector('.rs-d-close').addEventListener('click', closePaper);
    el.dialog.querySelector('.rs-d-close').focus();
}

function closePaper() {
    if (el.dialog.hidden) return;
    el.dialog.hidden = true;
    el.dialog.innerHTML = '';
    document.body.classList.remove('rs-locked');
    writeURL();
    lastFocus?.focus?.();
}

// ---------- Events ----------
function bind() {
    let timer = null;
    el.q.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            filters.q = el.q.value.trim();
            applyFilters();
        }, 300);
    });
    el.source.addEventListener('change', () => {
        filters.source = el.source.value;
        applyFilters();
    });
    el.country.addEventListener('change', () => {
        filters.country = el.country.value;
        applyFilters();
    });
    el.topics.addEventListener('click', (e) => {
        const b = e.target.closest('.rs-chip');
        if (!b) return;
        filters.topic = b.dataset.topic === filters.topic ? '' : b.dataset.topic;
        applyFilters();
    });
    el.clear.addEventListener('click', () => {
        Object.keys(filters).forEach((k) => (filters[k] = ''));
        el.q.value = '';
        el.source.value = '';
        el.country.value = '';
        applyFilters();
    });
    el.more.addEventListener('click', () => load({ append: true }));
    el.grid.addEventListener('click', (e) => {
        const b = e.target.closest('.rs-open');
        const p = b && papers.get(b.dataset.id);
        if (p) openPaper(p);
    });
    el.dialog.addEventListener('click', (e) => e.target === el.dialog && closePaper());
    document.addEventListener('keydown', (e) => e.key === 'Escape' && closePaper());
}

// ---------- Start ----------
async function init() {
    applyStatic();
    if (lang !== 'en') document.title = `${t('rs.title')} — NewsNetWide`;
    initLanguagePicker();
    initNavTools();
    initAuth();
    const paperId = readURL();
    el.q.value = filters.q;
    bind();
    try {
        const facets = await getJSON('/research/facets');
        renderTopics(facets);
        renderSelects(facets);
    } catch (err) {
        console.error('[research] facets failed', err);
    }
    syncChips();
    await load();
    if (paperId && /^\d+$/.test(paperId)) {
        try {
            const p = papers.get(paperId) || (await getJSON(`/research/${paperId}`)).data;
            papers.set(String(p.id), p);
            openPaper(p);
        } catch {
            writeURL();
        }
    }
}

init();
